import { supabase } from '../lib/supabase';

// ============================================
// ADMIN USER MANAGEMENT SERVICE
// ============================================

export const adminUserManagementService = {
  // Confirm user and assign profile
  async confirmUser(userId, profileId, notes) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({
        is_confirmed: true,
        confirmed_at: new Date()?.toISOString(),
        confirmed_by: user?.id,
        assigned_profile_id: profileId,
        confirmation_notes: notes,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', userId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Revoke confirmation
  async revokeConfirmation(userId) {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({
        is_confirmed: false,
        confirmed_at: null,
        confirmed_by: null,
        assigned_profile_id: null,
        confirmation_notes: null,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', userId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Get all unconfirmed users
  async getUnconfirmedUsers() {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('is_confirmed', false)
      ?.in('role', ['player', 'coach', 'keeper'])
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Get all confirmed users
  async getConfirmedUsers() {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*, assigned_profile:player_master_profiles!user_profiles_assigned_profile_id_fkey(full_name, primary_position)')
      ?.eq('is_confirmed', true)
      ?.in('role', ['player', 'coach', 'keeper'])
      ?.order('confirmed_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Unassign profile from user
  async unassignProfile(userId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({
        assigned_profile_id: null,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', userId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      email: obj?.email,
      fullName: obj?.full_name,
      role: obj?.role,
      avatarUrl: obj?.avatar_url,
      organization: obj?.organization,
      isActive: obj?.is_active,
      isConfirmed: obj?.is_confirmed,
      confirmedAt: obj?.confirmed_at,
      confirmedBy: obj?.confirmed_by,
      assignedProfileId: obj?.assigned_profile_id,
      confirmationNotes: obj?.confirmation_notes,
      assignedProfile: obj?.assigned_profile,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// ============================================
// ADMIN VISITOR TRACKING SERVICE
// ============================================

export const adminVisitorTrackingService = {
  async addVisitor(visitorData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const snakeCaseData = {
      player_id: visitorData?.playerId,
      visitor_name: visitorData?.visitorName,
      visitor_position: visitorData?.visitorPosition,
      visitor_club: visitorData?.visitorClub,
      visitor_country: visitorData?.visitorCountry,
      visitor_league: visitorData?.visitorLeague,
      visitor_region: visitorData?.visitorRegion,
      visit_date: visitorData?.visitDate || new Date()?.toISOString(),
      notes: visitorData?.notes,
      added_by: user?.id
    };
    
    const { data, error } = await supabase
      ?.from('admin_visitor_tracking')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async updateVisitor(id, visitorData) {
    const snakeCaseData = {
      visitor_name: visitorData?.visitorName,
      visitor_position: visitorData?.visitorPosition,
      visitor_club: visitorData?.visitorClub,
      visitor_country: visitorData?.visitorCountry,
      visitor_league: visitorData?.visitorLeague,
      visitor_region: visitorData?.visitorRegion,
      visit_date: visitorData?.visitDate,
      notes: visitorData?.notes
    };
    
    const { data, error } = await supabase
      ?.from('admin_visitor_tracking')
      ?.update(snakeCaseData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async deleteVisitor(id) {
    const { error } = await supabase
      ?.from('admin_visitor_tracking')
      ?.delete()
      ?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  async getVisitorsByPlayer(playerId) {
    const { data, error } = await supabase
      ?.from('admin_visitor_tracking')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.order('visit_date', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getVisitorStats(playerId) {
    const { data, error } = await supabase
      ?.from('admin_visitor_tracking')
      ?.select('visitor_country, visitor_league, visitor_position')
      ?.eq('player_id', playerId);
    
    if (error) throw error;
    
    const totalVisitors = data?.length || 0;
    const uniqueCountries = [...new Set(data?.map(v => v?.visitor_country)?.filter(Boolean))]?.length;
    const uniqueLeagues = [...new Set(data?.map(v => v?.visitor_league)?.filter(Boolean))]?.length;
    const uniqueClubs = [...new Set(data?.map(v => v?.visitor_club)?.filter(Boolean))]?.length;
    
    return {
      data: {
        totalVisitors,
        uniqueCountries,
        uniqueLeagues,
        uniqueClubs
      }
    };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      visitorName: obj?.visitor_name,
      visitorPosition: obj?.visitor_position,
      visitorClub: obj?.visitor_club,
      visitorCountry: obj?.visitor_country,
      visitorLeague: obj?.visitor_league,
      visitorRegion: obj?.visitor_region,
      visitDate: obj?.visit_date,
      notes: obj?.notes,
      addedBy: obj?.added_by,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// ============================================
// SCOUT/CLUB INTEREST SERVICE
// ============================================

export const scoutClubInterestService = {
  async expressInterest(playerId, description) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get user profile
    const { data: userProfile } = await supabase
      ?.from('user_profiles')
      ?.select('role, full_name, organization')
      ?.eq('id', user?.id)
      ?.single();
    
    const snakeCaseData = {
      player_id: playerId,
      interested_user_id: user?.id,
      interested_user_role: userProfile?.role,
      interested_user_name: userProfile?.full_name,
      interested_user_organization: userProfile?.organization,
      interest_description: description,
      is_interested: true
    };
    
    const { data, error } = await supabase
      ?.from('scout_club_interest')
      ?.upsert([snakeCaseData], { onConflict: 'player_id,interested_user_id' })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async removeInterest(playerId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { error } = await supabase
      ?.from('scout_club_interest')
      ?.delete()
      ?.eq('player_id', playerId)
      ?.eq('interested_user_id', user?.id);
    
    if (error) throw error;
    return { success: true };
  },

  async getInterestByPlayer(playerId) {
    const { data, error } = await supabase
      ?.from('scout_club_interest')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.eq('is_interested', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getMyInterests() {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      ?.from('scout_club_interest')
      ?.select('*, player:player_master_profiles(full_name, primary_position, current_market_value, profile_photo)')
      ?.eq('interested_user_id', user?.id)
      ?.eq('is_interested', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      interestedUserId: obj?.interested_user_id,
      interestedUserRole: obj?.interested_user_role,
      interestedUserName: obj?.interested_user_name,
      interestedUserOrganization: obj?.interested_user_organization,
      interestDescription: obj?.interest_description,
      isInterested: obj?.is_interested,
      player: obj?.player,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// ============================================
// ADMIN MESSAGING SERVICE
// ============================================

export const adminMessagingService = {
  async sendMessage(messageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const snakeCaseData = {
      created_by_admin_id: user?.id,
      target_user_id: messageData?.toUserId,
      target_player_id: messageData?.toPlayerId,
      message_type: messageData?.messageType || 'info',
      subject: messageData?.subject,
      message: messageData?.message
    };

    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async getMessagesByUser(userId) {
    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.select('*, created_by_admin:user_profiles!created_by_admin_id(full_name)')
      ?.eq('target_user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async markAsRead(messageId) {
    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })
      ?.eq('id', messageId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async getUnreadMessageCount() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { count, error } = await supabase
      ?.from('admin_messages')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('target_user_id', user?.id)
      ?.eq('is_read', false);
    
    if (error) throw error;
    return { count };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      fromAdminId: obj?.created_by_admin_id,
      toUserId: obj?.target_user_id,
      toPlayerId: obj?.target_player_id,
      subject: obj?.subject,
      message: obj?.message,
      isRead: obj?.is_read,
      readAt: obj?.read_at,
      fromAdmin: obj?.created_by_admin,
      createdAt: obj?.created_at
    };
  }
};

export const adminPlayerStatsService = {
  // Update player statistics in visitor_analytics_summary
  async updatePlayerStats(playerId, statsData) {
    const snakeCaseData = {
      player_id: playerId,
      total_views: statsData?.totalViews,
      scout_views: statsData?.scoutViews,
      club_views: statsData?.clubViews,
      agent_views: statsData?.agentViews,
      active_club_interest: statsData?.activeClubInterest,
      total_scout_feedback: statsData?.totalScoutFeedback,
      avg_scout_rating: statsData?.avgScoutRating,
      views_last_7_days: statsData?.viewsLast7Days,
      views_last_30_days: statsData?.viewsLast30Days,
      top_interested_positions: statsData?.topInterestedPositions || [],
      top_interested_leagues: statsData?.topInterestedLeagues || [],
      last_updated: new Date()?.toISOString()
    };

    const { data, error } = await supabase
      ?.from('visitor_analytics_summary')
      ?.upsert([snakeCaseData], { onConflict: 'player_id' })
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Get player statistics
  async getPlayerStats(playerId) {
    const { data, error } = await supabase
      ?.from('visitor_analytics_summary')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return { data: data ? this.toCamelCase(data) : null };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      totalViews: obj?.total_views,
      scoutViews: obj?.scout_views,
      clubViews: obj?.club_views,
      agentViews: obj?.agent_views,
      totalClubInterest: obj?.total_club_interest,
      activeClubInterest: obj?.active_club_interest,
      totalScoutFeedback: obj?.total_scout_feedback,
      avgScoutRating: obj?.avg_scout_rating,
      viewsLast7Days: obj?.views_last_7_days,
      viewsLast30Days: obj?.views_last_30_days,
      topInterestedPositions: obj?.top_interested_positions,
      topInterestedLeagues: obj?.top_interested_leagues,
      lastUpdated: obj?.last_updated
    };
  }
};

// ============================================
// ADMIN-PLAYER MESSAGING SERVICE (TWO-WAY)
// ============================================

export const adminPlayerMessagingService = {
  // Admin sends message to player
  async sendMessageToPlayer(messageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const snakeCaseData = {
      from_admin_id: user?.id,
      to_user_id: messageData?.toUserId,
      to_player_id: messageData?.toPlayerId,
      message_type: messageData?.messageType || 'info',
      subject: messageData?.subject,
      message: messageData?.message,
      sender_type: 'admin',
      is_read: false
    };

    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Player replies to admin message
  async sendPlayerReply(replyData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const snakeCaseData = {
      from_admin_id: user?.id,
      to_user_id: replyData?.toUserId,
      to_player_id: replyData?.toPlayerId,
      subject: replyData?.subject,
      message: replyData?.message,
      sender_type: 'player',
      reply_to_message_id: replyData?.replyToMessageId,
      is_read: false
    };

    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Get messages for a user (admin or player)
  async getMessagesForUser(userId) {
    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.select('*, created_by_admin:user_profiles!from_admin_id(full_name, email, role), target_user:user_profiles!to_user_id(full_name, email), target_player:player_master_profiles(full_name)')
      ?.or(`to_user_id.eq.${userId},from_admin_id.eq.${userId}`)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Get message thread (original message + all replies)
  async getMessageThread(messageId) {
    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.select('*, created_by_admin:user_profiles!from_admin_id(full_name, email, role), target_user:user_profiles!to_user_id(full_name, email)')
      ?.or(`id.eq.${messageId},reply_to_message_id.eq.${messageId}`)
      ?.order('created_at', { ascending: true });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Mark message as read
  async markAsRead(messageId) {
    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.update({ is_read: true, read_at: new Date()?.toISOString() })
      ?.eq('id', messageId)
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Get all messages sent by admin
  async getAdminSentMessages() {
    let user = null;
    try {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      user = authUser;
    } catch (e) {
      // ignore auth errors
    }

    if (!user) {
      // Try session as fallback
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        user = session?.user || null;
      } catch (e) {
        // ignore
      }
    }

    if (!user) {
      return { data: [] };
    }

    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.select('*, target_user:user_profiles!to_user_id(full_name, email), target_player:player_master_profiles(full_name)')
      ?.eq('from_admin_id', user?.id)
      ?.eq('sender_type', 'admin')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Get all player replies to admin
  async getPlayerReplies() {
    const { data, error } = await supabase
      ?.from('admin_messages')
      ?.select('*, created_by_admin:user_profiles!from_admin_id(full_name, email), target_user:user_profiles!to_user_id(full_name, email), reply_to:admin_messages!reply_to_message_id(subject)')
      ?.eq('sender_type', 'player')
      ?.not('reply_to_message_id', 'is', null)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      fromAdminId: obj?.from_admin_id,
      fromAdmin: obj?.created_by_admin,
      toUserId: obj?.to_user_id,
      toUser: obj?.target_user,
      toPlayerId: obj?.to_player_id,
      toPlayer: obj?.target_player,
      subject: obj?.subject,
      message: obj?.message,
      senderType: obj?.sender_type,
      replyToMessageId: obj?.reply_to_message_id,
      replyTo: obj?.reply_to,
      hasReplies: obj?.has_replies,
      isRead: obj?.is_read,
      readAt: obj?.read_at,
      createdAt: obj?.created_at
    };
  }
};

// Alias for backward compatibility
adminPlayerMessagingService.replyToMessage = adminPlayerMessagingService?.sendPlayerReply;